import { create } from "zustand";
import { consentApi } from "@/services/api/consent";

// Module-level flag: consent check runs ONCE per page load.
// Reset only on explicit logout via reset().
let _hasCheckedConsent = false;

/** Check if an error indicates an expired/invalid auth token. */
function isAuthError(err: unknown): boolean {
  if (err && typeof err === "object") {
    const e = err as Record<string, unknown>;
    if (e.code === "HTTP_401" || e.status === 401) return true;
    if (typeof e.message === "string") {
      const msg = e.message as string;
      if (
        msg.includes("401") ||
        msg === "Unauthorized" ||
        msg.includes("invalid_grant")
      )
        return true;
    }
  }
  return false;
}

interface ConsentState {
  hasAccepted: boolean | null;
  passwordMustChange: boolean;
  approvalStatus: "approved" | "pending" | "rejected" | null;
  isLoading: boolean;
  hasError: boolean;
  isSessionExpired: boolean;
  checkConsent: () => Promise<void>;
  retryConsent: () => Promise<void>;
  markAccepted: () => void;
  markPasswordChanged: () => void;
  setApprovalPending: () => void;
  reset: () => void;
}

export const useConsentStore = create<ConsentState>()((set, get) => ({
  hasAccepted: null,
  passwordMustChange: false,
  approvalStatus: null,
  isLoading: false,
  hasError: false,
  isSessionExpired: false,

  checkConsent: async () => {
    if (_hasCheckedConsent) return; // Module-level: once per session
    _hasCheckedConsent = true;

    set({ isLoading: true, hasError: false, isSessionExpired: false });
    try {
      const status = await consentApi.getStatus();
      console.log("[ConsentStore] getStatus response:", JSON.stringify(status));
      set({
        hasAccepted: status.accepted,
        passwordMustChange: status.password_must_change ?? false,
        approvalStatus: status.approval_status ?? "approved",
        isLoading: false,
      });
      console.log(
        "[ConsentStore] hasAccepted=%s, passwordMustChange=%s, approvalStatus=%s",
        status.accepted,
        status.password_must_change,
        status.approval_status,
      );
    } catch (err) {
      console.error("[ConsentStore] getStatus FAILED:", err);
      if (isAuthError(err)) {
        console.warn(
          "[ConsentStore] Token expired — triggering session expired",
        );
        set({ hasAccepted: null, isLoading: false, isSessionExpired: true });
      } else {
        set({ hasAccepted: null, isLoading: false, hasError: true });
      }
    }
  },

  retryConsent: async () => {
    _hasCheckedConsent = false;
    set({ hasError: false });
    return get().checkConsent();
  },

  markAccepted: () => set({ hasAccepted: true, hasError: false }),

  markPasswordChanged: () => set({ passwordMustChange: false }),

  setApprovalPending: () => set({ approvalStatus: "pending" }),

  reset: () => {
    _hasCheckedConsent = false;
    set({
      hasAccepted: null,
      passwordMustChange: false,
      approvalStatus: null,
      isLoading: false,
      hasError: false,
    });
  },
}));
